import React, { useState, useEffect } from 'react';
import {
  IconX,
  IconBolt,
  IconDeviceGamepad2,
  IconSettings,
  IconChevronLeft,
  IconCheck
} from '@tabler/icons-react';
import { GameIcon, GAMES } from '../../constants/games';
import { useNodeSelection } from '../../hooks/useNodeSelection';
import LocationStep from './LocationStep';

const CreateTunnelModal = ({ onClose, onCreate }) => {
  const [step, setStep] = useState('choose'); // 'choose', 'game', 'custom', 'location'
  const [selectedGame, setSelectedGame] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tunnelConfig, setTunnelConfig] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    localHost: 'localhost',
    localPort: '',
    protocol: 'TCP',
  });

  const {
    nodes,
    nodesLoading,
    selectedNode,
    setSelectedNode,
    fetchAvailableNodes,
    reset: resetNodeSelection
  } = useNodeSelection();

  useEffect(() => {
    fetchAvailableNodes();
  }, [fetchAvailableNodes]);

  const handleSelectGame = (game) => {
    setSelectedGame(game);
  };

  const handleGameNext = () => {
    if (!selectedGame || !formData.name.trim()) return;
    
    setTunnelConfig({
      name: formData.name,
      game: selectedGame.id,
      local_port: selectedGame.port,
      protocol: selectedGame.protocol.toLowerCase().replace('+', '_'),
    });
    setStep('location');
  };

  const handleCustomNext = () => {
    if (!formData.name.trim() || !formData.localPort || !formData.localHost.trim()) return;
    
    setTunnelConfig({
      name: formData.name,
      game: 'custom',
      local_ip: formData.localHost.trim(),
      local_port: parseInt(formData.localPort),
      protocol: formData.protocol.toLowerCase().replace('+', '_'),
    });
    setStep('location');
  };

  const handleCreateTunnel = async () => {
    if (!tunnelConfig) return;
    
    setLoading(true);
    try {
      const tunnelData = { ...tunnelConfig };
      
      if (selectedNode) {
        tunnelData.node_id = selectedNode.id;
      }
      
      await onCreate(tunnelData);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (selectedGame) {
      setStep('game');
    } else {
      setStep('custom');
    }
  };

  const handleClose = () => {
    setStep('choose');
    setSelectedGame(null);
    setTunnelConfig(null);
    setFormData({
      name: '',
      localHost: 'localhost',
      localPort: '',
      protocol: 'TCP',
    });
    resetNodeSelection();
    onClose();
  };

  // Step: Choose type
  if (step === 'choose') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-950/80 backdrop-blur-sm">
        <div className="bg-dark-900 border border-dark-800 rounded-2xl max-w-lg w-full p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-yellow-400 rounded-xl flex items-center justify-center">
                <IconBolt className="w-5 h-5 text-black" />
              </div>
              <h2 className="text-xl font-semibold text-white">Новый туннель</h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-dark-500 hover:text-white hover:bg-dark-800 rounded-lg transition-colors"
            >
              <IconX className="w-5 h-5" />
            </button>
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={() => setStep('game')}
              className="group p-6 bg-dark-950 border border-dark-800 rounded-xl hover:border-primary-500/50 transition-all text-left"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-primary-500/20 to-primary-600/10 rounded-xl flex items-center justify-center mb-4 group-hover:from-primary-500/30 group-hover:to-primary-600/20 transition-colors">
                <IconDeviceGamepad2 className="w-7 h-7 text-primary-500" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Выбрать игру</h3>
              <p className="text-dark-500 text-sm">
                Быстрая настройка для популярных игр с автоматическим определением порта и протокола
              </p>
            </button>

            <button
              onClick={() => setStep('custom')}
              className="group p-6 bg-dark-950 border border-dark-800 rounded-xl hover:border-dark-700 transition-all text-left"
            >
              <div className="w-14 h-14 bg-dark-800 rounded-xl flex items-center justify-center mb-4 group-hover:bg-dark-700 transition-colors">
                <IconSettings className="w-7 h-7 text-dark-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Настроить вручную</h3>
              <p className="text-dark-500 text-sm">
                Указать порт и протокол самостоятельно для любого сервиса или приложения
              </p>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step: Select game
  if (step === 'game') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-950/80 backdrop-blur-sm">
        <div className="bg-dark-900 border border-dark-800 rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setStep('choose'); setSelectedGame(null); }}
                className="p-2 text-dark-500 hover:text-white hover:bg-dark-800 rounded-lg transition-colors"
              >
                <IconChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-semibold text-white">Выберите игру</h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-dark-500 hover:text-white hover:bg-dark-800 rounded-lg transition-colors"
            >
              <IconX className="w-5 h-5" />
            </button>
          </div>

          {/* Games grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 overflow-y-auto flex-1 mb-6">
            {GAMES.map(game => (
              <button
                key={game.id}
                onClick={() => handleSelectGame(game)}
                className={`p-4 rounded-xl transition-all text-center border ${
                  selectedGame?.id === game.id
                    ? 'bg-primary-500/10 border-primary-500'
                    : 'bg-dark-950 border-dark-800 hover:border-dark-700'
                }`}
              >
                <div className="flex justify-center mb-2">
                  <GameIcon gameId={game.id} name={game.name} className="w-12 h-12" />
                </div>
                <span className="text-white text-sm font-medium block truncate">{game.name}</span>
                <span className="text-dark-600 text-xs">{game.protocol} :{game.port}</span>
              </button>
            ))}
          </div>

          {/* Name input + Next button */}
          {selectedGame && (
            <div className="pt-4 border-t border-dark-800 space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  Название сервера
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={`Мой ${selectedGame.name} сервер`}
                  className="w-full px-4 py-3 bg-dark-950 border border-dark-800 rounded-lg text-white placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors"
                />
              </div>
              <button
                onClick={handleGameNext}
                disabled={!formData.name.trim()}
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 rounded-lg font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <span>Далее</span>
                <IconChevronLeft className="w-5 h-5 rotate-180" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Step: Custom configuration
  if (step === 'custom') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-950/80 backdrop-blur-sm">
        <div className="bg-dark-900 border border-dark-800 rounded-2xl max-w-md w-full p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setStep('choose')}
                className="p-2 text-dark-500 hover:text-white hover:bg-dark-800 rounded-lg transition-colors"
              >
                <IconChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-semibold text-white">Настройка вручную</h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-dark-500 hover:text-white hover:bg-dark-800 rounded-lg transition-colors"
            >
              <IconX className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-5">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-dark-300 mb-2">
                Название
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Мой сервер"
                className="w-full px-4 py-3 bg-dark-950 border border-dark-800 rounded-lg text-white placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors"
              />
            </div>

            {/* Host */}
            <div>
              <label className="block text-sm font-medium text-dark-300 mb-2">
                Локальный хост / IP
              </label>
              <input
                type="text"
                value={formData.localHost}
                onChange={(e) => setFormData({ ...formData, localHost: e.target.value })}
                placeholder="localhost или 127.0.0.1"
                className="w-full px-4 py-3 bg-dark-950 border border-dark-800 rounded-lg text-white placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors"
              />
            </div>

            {/* Port */}
            <div>
              <label className="block text-sm font-medium text-dark-300 mb-2">
                Локальный порт
              </label>
              <input
                type="number"
                value={formData.localPort}
                onChange={(e) => setFormData({ ...formData, localPort: e.target.value })}
                placeholder="25565"
                className="w-full px-4 py-3 bg-dark-950 border border-dark-800 rounded-lg text-white placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors"
              />
            </div>

            {/* Protocol */}
            <div>
              <label className="block text-sm font-medium text-dark-300 mb-2">
                Протокол
              </label>
              <div className="grid grid-cols-3 gap-2">
                {['TCP', 'UDP', 'TCP+UDP'].map(protocol => (
                  <button
                    key={protocol}
                    type="button"
                    onClick={() => setFormData({ ...formData, protocol })}
                    className={`py-3 rounded-lg text-sm font-medium transition-colors border ${
                      formData.protocol === protocol
                        ? 'bg-primary-500 border-primary-500 text-white'
                        : 'bg-dark-850 border-dark-800 text-dark-400 hover:bg-dark-800 hover:border-dark-700'
                    }`}
                  >
                    {protocol}
                  </button>
                ))}
              </div>
            </div>

            {/* Submit */}
            <button
              onClick={handleCustomNext}
              disabled={!formData.name.trim() || !formData.localPort || !formData.localHost.trim()}
              className="w-full py-3 bg-primary-500 hover:bg-primary-600 rounded-lg font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <span>Далее</span>
              <IconChevronLeft className="w-5 h-5 rotate-180" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step: Location (node selection)
  return (
    <LocationStep
      nodes={nodes}
      nodesLoading={nodesLoading}
      selectedNode={selectedNode}
      onSelectNode={setSelectedNode}
      onCreate={handleCreateTunnel}
      loading={loading}
      onBack={handleBack}
      onClose={handleClose}
    />
  );
};

export default CreateTunnelModal;